/**
 * A minimal wrapper over the YouTube IFrame Player API.
 *
 * The Academy needs two things a plain <iframe> cannot give it: where the
 * viewer stopped watching (so the hero can say "pick up where you left off")
 * and a signal when the video finishes (so a lesson marks itself complete
 * without the viewer having to tick a box).
 *
 * The API script is loaded once per page and shared. It is fetched from
 * youtube.com rather than youtube-nocookie.com because only the former serves
 * it — the player it creates still points at the nocookie host, which is what
 * the embed URL in lib/academy.ts builds.
 *
 * Everything here degrades quietly: if the script is blocked (an ad blocker, an
 * offline tab) the hook simply never reports progress and the lesson still
 * plays, because the iframe the player attaches to is a normal embed.
 */
import { useEffect, useRef } from "react";

/** How often to report the play position while a video is playing. */
const PROGRESS_INTERVAL_MS = 5000;

// Only the slice of the API surface this file touches. There is no
// @types/youtube in the project and pulling one in for four methods would be a
// dependency for its own sake.
interface YTPlayer {
  getCurrentTime: () => number;
  destroy: () => void;
}

interface YTStateChange {
  data: number;
}

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: HTMLElement | string,
        options: {
          events?: {
            onStateChange?: (e: YTStateChange) => void;
            onReady?: () => void;
          };
        },
      ) => YTPlayer;
      PlayerState?: { ENDED: number; PLAYING: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

const API_SRC = "https://www.youtube.com/iframe_api";
const ENDED = 0;
const PLAYING = 1;

let apiPromise: Promise<void> | null = null;

/** Load the IFrame API once, shared by every player on the page. */
function loadApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<void>((resolve, reject) => {
    // The API calls this global when it finishes loading. Chain rather than
    // replace: another consumer may already have registered one.
    const prior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prior?.();
      resolve();
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${API_SRC}"]`);
    if (existing) return; // already in flight — the callback above will fire

    const script = document.createElement("script");
    script.src = API_SRC;
    script.async = true;
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      apiPromise = null;
      reject(new Error("YouTube player unavailable"));
    };
    document.head.appendChild(script);
  });

  return apiPromise;
}

export interface YouTubePlayerOptions {
  /** The iframe element to attach to. Null while the dialog is closed. */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Re-attaches whenever this changes, i.e. when a different lesson opens. */
  videoId: string;
  /** Called every few seconds while playing, with the position in seconds. */
  onProgress?: (seconds: number) => void;
  /** Called once when the video reaches the end. */
  onEnded?: () => void;
}

/**
 * Attach a player to an already-rendered embed iframe.
 *
 * The iframe is rendered by React (so the `src` and the sandboxing stay in the
 * component) and this only wires the API to it — which is why `videoId` is used
 * as an attach key rather than being passed to the player as content.
 */
export function useYouTubePlayer({
  iframeRef,
  videoId,
  onProgress,
  onEnded,
}: YouTubePlayerOptions): void {
  // Held in refs so a changing callback identity doesn't tear down the player
  // mid-video — the attach effect below must depend only on which lesson is
  // open. Updated in an effect rather than during render: a ref write during
  // render is not safe under concurrent rendering, which may discard the pass.
  const onProgressRef = useRef(onProgress);
  const onEndedRef = useRef(onEnded);

  useEffect(() => {
    onProgressRef.current = onProgress;
    onEndedRef.current = onEnded;
  }, [onProgress, onEnded]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !videoId) return;

    let player: YTPlayer | null = null;
    let timer: number | null = null;
    let cancelled = false;

    const stopTimer = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const report = () => {
      if (!player) return;
      try {
        const seconds = Math.floor(player.getCurrentTime());
        if (seconds > 0) onProgressRef.current?.(seconds);
      } catch {
        /* player torn down between the tick and the read */
      }
    };

    loadApi()
      .then(() => {
        if (cancelled || !window.YT?.Player) return;
        player = new window.YT.Player(iframe, {
          events: {
            onStateChange: (e) => {
              if (e.data === PLAYING) {
                stopTimer();
                timer = window.setInterval(report, PROGRESS_INTERVAL_MS);
              } else {
                stopTimer();
                // Paused, buffering or ended — bank the position now rather
                // than losing the last few seconds of the interval.
                report();
                if (e.data === ENDED) onEndedRef.current?.();
              }
            },
          },
        });
      })
      .catch(() => {
        /* blocked or offline — the embed still plays, just untracked */
      });

    return () => {
      cancelled = true;
      stopTimer();
      // One last read before teardown, so closing the dialog saves the spot.
      report();
      try {
        player?.destroy();
      } catch {
        /* already gone */
      }
    };
  }, [iframeRef, videoId]);
}
